import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent } from "@/hooks/use-cookie-consent";
import { localizedPath } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { Cookie, Lock, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { analyticsText } from "../../../shared/network-analytics-i18n";
const ACCOUNT_CONSENT_KEY = "enkey.analytics.account-consent.v1";

// Categories rendered in the preferences dialog. "necessary" is locked on.
type ToggleCategory = "analytics" | "marketing";

export default function CookieBanner() {
  const { t } = useTranslation("common");
  const { lng } = useParams<{ lng: string }>();
  const locale = lng ?? "it";
  const analyticsLabel = analyticsText(locale);
  const [accountConsent, setAccountConsent] = useState(false);
  const {
    consent,
    isDecided,
    preferencesOpen,
    openPreferences,
    closePreferences,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useCookieConsent();

  // Local draft toggles for the preferences dialog, seeded from current consent.
  const [draft, setDraft] = useState<Record<ToggleCategory, boolean>>({
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
  });

  // When opening preferences, reflect the latest stored consent in the draft.
  const handleOpenPreferences = () => {
    try {
      setAccountConsent(localStorage.getItem(ACCOUNT_CONSENT_KEY) === "true");
    } catch {
      setAccountConsent(false);
    }
    setDraft({
      analytics: consent?.analytics ?? false,
      marketing: consent?.marketing ?? false,
    });
    openPreferences();
  };

  const categories: {
    key: ToggleCategory | "necessary";
    icon: typeof ShieldCheck;
    locked?: boolean;
  }[] = [
    { key: "necessary", icon: Lock, locked: true },
    { key: "analytics", icon: ShieldCheck },
    { key: "marketing", icon: Cookie },
  ];

  return (
    <>
      {/* Bottom banner: shown until the visitor makes a choice. */}
      {!isDecided && (
        <div
          data-print-hidden
          className="enkey-ui-enter fixed inset-x-0 bottom-0 z-50 max-w-full overflow-x-clip px-[max(.75rem,env(safe-area-inset-left))] pb-[max(.75rem,env(safe-area-inset-bottom))] sm:px-[max(1rem,env(safe-area-inset-left))] sm:pb-[max(1rem,env(safe-area-inset-bottom))]"
          role="region"
          aria-label={t("cookie.banner.title")}
        >
          <div className="mx-auto max-h-[calc(100dvh-1rem)] w-full max-w-5xl overflow-x-hidden overflow-y-auto overscroll-contain rounded-2xl border border-border bg-card shadow-2xl shadow-black/20">
            <div className="flex min-w-0 flex-col gap-3 p-4 sm:gap-4 sm:p-6 lg:flex-row lg:items-center lg:gap-6">
              <div className="flex items-start gap-3">
                <span className="hidden h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary sm:grid">
                  <Cookie className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h2 className="font-display text-base font-semibold uppercase tracking-tight">
                    {t("cookie.banner.title")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("cookie.banner.description")}{" "}
                    <Link
                      to={localizedPath("cookie", locale)}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {t("footer.cookie")}
                    </Link>
                    {" · "}
                    <Link
                      to={localizedPath("privacy", locale)}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {t("footer.privacy")}
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="grid min-w-0 grid-cols-2 gap-2 sm:flex sm:flex-row lg:ml-auto lg:shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenPreferences}
                  className="col-span-2 w-full min-w-0 cursor-pointer sm:w-auto"
                >
                  {t("cookie.banner.customize")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={rejectAll}
                  className="w-full min-w-0 cursor-pointer sm:w-auto"
                >
                  {t("cookie.banner.reject")}
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="w-full min-w-0 cursor-pointer sm:w-auto"
                >
                  {t("cookie.banner.accept")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Persistent privacy control: available after the first choice without
          forcing the visitor to hunt through the footer. */}
      {isDecided && !preferencesOpen && (
        <button
          data-print-hidden
          type="button"
          onClick={handleOpenPreferences}
          className="enkey-ui-pop fixed bottom-0 left-[max(1rem,env(safe-area-inset-left))] z-40 grid h-11 w-12 cursor-pointer place-items-center rounded-t-xl border border-b-0 border-border bg-card/95 text-primary shadow-[0_-4px_16px_rgba(0,0,0,.14)] backdrop-blur transition-colors hover:border-primary/50 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t("cookie.manage")}
          title={t("cookie.manage")}
        >
          <Cookie className="h-5 w-5" />
        </button>
      )}

      {/* Preferences dialog: reachable from the banner and the footer link. */}
      <Dialog
        open={preferencesOpen}
        onOpenChange={(open) => (open ? openPreferences() : closePreferences())}
      >
        <DialogContent data-print-hidden className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">
              {t("cookie.prefs.title")}
            </DialogTitle>
            <DialogDescription>
              {t("cookie.prefs.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {categories.map(({ key, icon: Icon, locked }) => (
              <div
                key={key}
                className="flex items-start justify-between gap-4 rounded-xl border border-border bg-muted/30 p-4"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
                      locked
                        ? "bg-muted text-muted-foreground"
                        : "bg-primary/10 text-primary",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">
                      {t(`cookie.category.${key}.title`)}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t(`cookie.category.${key}.description`)}
                    </p>
                  </div>
                </div>
                {key === "necessary" ? (
                  <span className="mt-1 shrink-0 rounded-md bg-muted px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("cookie.category.necessary.always")}
                  </span>
                ) : (
                  <Switch
                    checked={draft[key]}
                    onCheckedChange={(checked) =>
                      setDraft((prev) => ({ ...prev, [key]: checked }))
                    }
                    aria-label={t(`cookie.category.${key}.title`)}
                    className="mt-1 cursor-pointer"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <label className="mb-3 block text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={accountConsent && draft.analytics}
                disabled={!draft.analytics}
                onChange={(event) => setAccountConsent(event.target.checked)}
                className="mr-2"
              />
              {analyticsLabel("accountConsent")}
              <span className="mt-1 block text-xs text-muted-foreground">
                {analyticsLabel("accountConsentDescription")}
              </span>
            </label>
            <Button
              variant="secondary"
              size="sm"
              onClick={rejectAll}
              className="cursor-pointer"
            >
              {t("cookie.banner.reject")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                try {
                  localStorage.setItem(
                    ACCOUNT_CONSENT_KEY,
                    String(draft.analytics && accountConsent),
                  );
                } catch {
                  /* optional storage */
                }
                savePreferences(draft);
              }}
              className="cursor-pointer"
            >
              {t("cookie.prefs.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
